import { type TypedDocumentNode, gql } from '@apollo/client';
import styled from 'styled-components';

import { ManagerProgressionTaskSlug } from '@sorare/core/src/__generated__/globalTypes';
import { ToastBase } from '@sorare/core/src/atoms/snackbar/ToastBase';
import { isType } from '@sorare/core/src/lib/gql';
import { tabletAndAbove } from '@sorare/core/src/style/mediaQuery';

import { CollectionToastContent } from './CollectionToastContent';
import { DailyBoostToastContent } from './DailyBoostToastContent';
import type { MissionToast_taskInterface } from './__generated__/index.graphql';

const StyledToastBase = styled(ToastBase)`
  width: 100%;
  align-self: center;

  @media ${tabletAndAbove} {
    width: calc(60 * var(--unit));
  }
`;

type Props = {
  open: boolean;
  onClose: () => void;
  task: MissionToast_taskInterface;
  previousProgress: number;
};
export const MissionToast = ({
  open,
  onClose,
  task,
  previousProgress,
}: Props) => {
  const { parentTask } = task;
  const isDailyBoostTask =
    parentTask?.name === ManagerProgressionTaskSlug.DAILY_ACTION;
  const isCollectionTask =
    isType(task, 'CardsCountTask') ||
    isType(task, 'CompleteCollectionsCountTask');

  const onOpenChange = (o: boolean) => {
    if (!o) {
      onClose();
    }
  };

  return (
    <StyledToastBase onOpenChange={onOpenChange} duration={5000} open={open}>
      {!!isDailyBoostTask && isType(task, 'Task') && (
        <DailyBoostToastContent task={task} />
      )}
      {!!isCollectionTask && (
        <CollectionToastContent
          task={task}
          previousProgress={previousProgress}
          onClose={onClose}
        />
      )}
    </StyledToastBase>
  );
};

MissionToast.fragments = {
  taskInterface: gql`
    fragment MissionToast_taskInterface on TaskInterface {
      id
      title
      parentTask {
        id
        name
      }
      ...DailyBoostToastContent_task
      ...CollectionToastContent_taskInterface
    }
    ${DailyBoostToastContent.fragments.task}
    ${CollectionToastContent.fragments.taskInterface}
  ` as TypedDocumentNode<MissionToast_taskInterface>,
};
